import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import type { ExplorationSnapshot } from 'features/drilldown/data/savedExplorationState/explorationMutation'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback, useMemo } from 'react'

/**
 * Client for the gong-agent-service generic chat backend
 * (`/api/v1/chat/*`). Sessions and messages are persisted server-side in
 * Rolodex; each turn replays history and can return structured `actions`
 * (e.g. `create_panels`) for the client to execute.
 */

export interface ChatAction {
  type: string
  payload: unknown
}

export interface ChatMessageResponse {
  id: string
  session_id: string
  role: 'user' | 'assistant'
  sequence: number
  content: string
  created_at: number
  status?: string | null
  actions?: ReadonlyArray<ChatAction>
  documents?: ReadonlyArray<Record<string, unknown>>
  resolved_unit_ids?: ReadonlyArray<string>
}

export interface ChatSession {
  id: string
  org_id: string
  user_id: string
  title?: string | null
  status: string
  message_count: number
  created_at: number
  updated_at: number
  preview?: string | null
}

export interface SendMessageArgs {
  sessionId: string
  content: string
  unitIds?: ReadonlyArray<string>
  clientContext?: string
  explorationSnapshot?: ExplorationSnapshot
  /** Used by the service to deduplicate retries; it is not part of the snapshot. */
  requestId?: string
  enablePanelCreation?: boolean
}

export interface ChatClient {
  ensureSession: (orgId: string, sessionId?: string) => Promise<string>
  listSessions: (
    orgId: string,
    limit?: number
  ) => Promise<ReadonlyArray<ChatSession>>
  loadMessages: (
    sessionId: string
  ) => Promise<ReadonlyArray<ChatMessageResponse>>
  sendMessage: (args: SendMessageArgs) => Promise<ChatMessageResponse>
  deleteSession: (sessionId: string) => Promise<void>
}

/**
 * Thrown to abort a chat request when the backend URL is not configured. The
 * user has already been shown a popup by this point, so callers can treat it
 * as a silent, expected abort rather than an unexpected failure.
 */
export class ChatUnavailableError extends Error {
  constructor() {
    super('GONG_AGENT_URL is not configured.')
    this.name = 'ChatUnavailableError'
  }
}

export function useChat(): ChatClient {
  const { GONG_AGENT_URL } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  const { errorNotification } = useGeckoNotifications()

  const requireBase = useCallback((): string => {
    if (!GONG_AGENT_URL) {
      errorNotification(
        'AI chat is unavailable right now because it has not been configured. Please try again later.'
      )
      throw new ChatUnavailableError()
    }
    return `${GONG_AGENT_URL}/api/v1/chat`
  }, [GONG_AGENT_URL, errorNotification])

  const ensureSession = useCallback(
    async (orgId: string, sessionId?: string): Promise<string> => {
      const response = await axios.post<{
        session_id: string
        created: boolean
      }>(`${requireBase()}/sessions`, {
        org_id: orgId,
        session_id: sessionId,
      })
      return response.data.session_id
    },
    [axios, requireBase]
  )

  const listSessions = useCallback(
    async (orgId: string, limit = 100): Promise<ReadonlyArray<ChatSession>> => {
      const response = await axios.get<ChatSession[]>(
        `${requireBase()}/sessions`,
        { params: { org_id: orgId, limit } }
      )
      return response.data.filter((session) => session.status === 'active')
    },
    [axios, requireBase]
  )

  const loadMessages = useCallback(
    async (sessionId: string): Promise<ReadonlyArray<ChatMessageResponse>> => {
      const response = await axios.get<{
        messages: ChatMessageResponse[]
        total: number
      }>(`${requireBase()}/sessions/${sessionId}/messages`)
      return response.data.messages
    },
    [axios, requireBase]
  )

  const sendMessage = useCallback(
    async ({
      sessionId,
      content,
      unitIds,
      clientContext,
      explorationSnapshot,
      requestId,
      enablePanelCreation,
    }: SendMessageArgs): Promise<ChatMessageResponse> => {
      const response = await axios.post<ChatMessageResponse>(
        `${requireBase()}/sessions/${sessionId}/messages`,
        {
          content,
          unit_ids: unitIds ?? [],
          client_context: clientContext,
          // Backend-owned wire name retained for compatibility with the Gong
          // agent contract; frontend code consistently calls this a snapshot.
          dashboard_context: explorationSnapshot,
          request_id: requestId,
          enable_panel_creation: enablePanelCreation ?? false,
        }
      )
      return response.data
    },
    [axios, requireBase]
  )

  const deleteSession = useCallback(
    async (sessionId: string): Promise<void> => {
      await axios.delete(`${requireBase()}/sessions/${sessionId}`)
    },
    [axios, requireBase]
  )

  return useMemo(
    () => ({
      ensureSession,
      listSessions,
      loadMessages,
      sendMessage,
      deleteSession,
    }),
    [ensureSession, listSessions, loadMessages, sendMessage, deleteSession]
  )
}
